import { Box } from '@material-ui/core';
import {
  Currency,
  EmploymentStatus,
  formatMoney,
  getMoneySymbolByCurrency,
  ProfessionalDetails,
  ProfessionalDetailsSchema,
} from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  CIF_REQUIRED_EMPLOYMENT_STATUS,
  COMPANY_ACTIVITY_TYPE_ARRAY,
  EMPLOYMENT_STATUS_FILTER,
  EMPLOYMENT_STATUS_TYPE_ARRAY,
  PROFESSIONAL_POSITION_TYPE_ARRAY,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/constants';
import { seniorityDateFormatter } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/utils/seniorityDateToMonthsAndYears';
import { Font } from '@/components/shared/components/Font';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { FormGridOptions } from '@/components/shared/components/FormGridContainer/types/formGridOptions';
import { AutocompleteSelectFormField } from '@/components/shared/components/NewContextForms/AutocompleteSelectFormField';
import { DateFormField } from '@/components/shared/components/NewContextForms/DateFormField';
import { NumberFormField } from '@/components/shared/components/NewContextForms/NumberFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { getBlankDefaultValuesFromZodSchema } from '@/utils/getBlankDefaultValuesFromZodSchema';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';

export interface ProfessionalDetailsFormProps extends FormGridOptions {
  additionalProfessionalDetailsRequired: boolean;
  isCompanyCifRequired?: boolean;
  currency?: Currency;
  professionalDetails?: ProfessionalDetails;
}

export function ProfessionalDetailsForm({
  additionalProfessionalDetailsRequired,
  isCompanyCifRequired = false,
  currency,
  professionalDetails = getBlankDefaultValuesFromZodSchema(ProfessionalDetailsSchema, currency),
  fullWidth = false,
  fieldVariant,
  direction,
}: ProfessionalDetailsFormProps) {
  const { getTextIdTranslation } = useTranslation();

  const watch = useFormContext()?.watch;
  const setValue = useFormContext()?.setValue;
  const resetField = useFormContext()?.resetField;

  const employmentStatus = watch ? watch('employmentStatus') : professionalDetails.employmentStatus;
  const showProfessionalDetailsField = EMPLOYMENT_STATUS_FILTER.includes(employmentStatus);
  const showCompanyCifDetailsField =
    CIF_REQUIRED_EMPLOYMENT_STATUS.find((element) => element === employmentStatus) &&
    additionalProfessionalDetailsRequired;
  const isTemporaryEmployed = employmentStatus === EmploymentStatus.TEMPORARY_EMPLOYED;
  const isSelfEmployed = employmentStatus === EmploymentStatus.SELF_EMPLOYED;

  useEffect(() => {
    if (showProfessionalDetailsField && resetField) {
      resetField('professionalPosition');
      resetField('companyName');
      resetField('companyActivity');
      resetField('companyCity');
      resetField('seniority');
      resetField('corporateIdNumber');
    } else if (setValue) {
      setValue('professionalPosition', null);
      setValue('companyName', null);
      setValue('companyActivity', null);
      setValue('companyCity', null);
      setValue('seniority', null);
      setValue('corporateIdNumber', null);
    }
  }, [showProfessionalDetailsField]);

  useEffect(() => {
    if (isTemporaryEmployed && resetField) {
      resetField('contractDateEnd');
    } else if (setValue) {
      setValue('contractDateEnd', null);
    }
  }, [isTemporaryEmployed]);

  useEffect(() => {
    if (isSelfEmployed && setValue) {
      setValue('companyName', `${watch('firstName')} ${watch('lastName')}`);
    }
  }, [isSelfEmployed]);

  return (
    <FormGridContainer fieldVariant={fieldVariant} fullWidth={fullWidth} direction={direction}>
      <AutocompleteSelectFormField
        name={'employmentStatus'}
        label={'EMPLOYMENT_STATUS'}
        value={getTextIdTranslation(professionalDetails.employmentStatus)}
        selectOptions={EMPLOYMENT_STATUS_TYPE_ARRAY(getTextIdTranslation)}
        fieldVariant={fieldVariant}
      />
      {showProfessionalDetailsField && (
        <AutocompleteSelectFormField
          name={'professionalPosition'}
          label={'PROFESSIONAL_POSITION'}
          value={
            professionalDetails.professionalPosition && getTextIdTranslation(professionalDetails.professionalPosition)
          }
          selectOptions={PROFESSIONAL_POSITION_TYPE_ARRAY(getTextIdTranslation)}
          fieldVariant={fieldVariant}
        />
      )}
      {showProfessionalDetailsField && (
        <TextFormField
          name={'companyName'}
          label={'COMPANY_NAME'}
          value={professionalDetails.companyInfo?.name}
          fieldVariant={fieldVariant}
        />
      )}
      {showProfessionalDetailsField && (
        <AutocompleteSelectFormField
          name={'companyActivity'}
          label={'COMPANY_ACTIVITY'}
          value={
            professionalDetails.companyInfo?.activity && getTextIdTranslation(professionalDetails.companyInfo.activity)
          }
          selectOptions={COMPANY_ACTIVITY_TYPE_ARRAY(getTextIdTranslation)}
          fieldVariant={fieldVariant}
        />
      )}
      {showProfessionalDetailsField && (
        <TextFormField
          name={'companyCity'}
          label={'COMPANY_CITY'}
          value={professionalDetails.companyInfo?.city}
          fieldVariant={fieldVariant}
        />
      )}
      {showProfessionalDetailsField && showCompanyCifDetailsField && (
        <Box>
          <TextFormField
            markAsOptional={!isCompanyCifRequired}
            name={'corporateIdNumber'}
            label={'COMPANY_VAT'}
            value={professionalDetails.companyInfo?.corporateIdNumber}
            fieldVariant={fieldVariant}
          />
          {fieldVariant === FieldVariant.form && isCompanyCifRequired && (
            <Font variant={'caption'} color={'textSecondary'}>
              <Text tId="COMPANY_VAT_DESCRIPTION" />
            </Font>
          )}
        </Box>
      )}
      {showProfessionalDetailsField && (
        <DateFormField
          name={'seniority'}
          label={'SENIORITY'}
          placeholder={getTextIdTranslation('DATE_FORMAT_MONTH_YEAR')}
          views={['year', 'month']}
          format={'MM/yyyy'}
          value={seniorityDateFormatter.monthsAndYearsToDate(
            professionalDetails.companyInfo?.seniorityMonths,
            professionalDetails.companyInfo?.seniorityYears,
          )}
          fieldVariant={fieldVariant}
          disableFuture
        />
      )}
      {isTemporaryEmployed && (
        <DateFormField
          name={'contractDateEnd'}
          label={'DATE_CONTRACT_END'}
          value={professionalDetails.companyInfo?.contractDateEnd}
          fieldVariant={fieldVariant}
          disablePast
        />
      )}
      {additionalProfessionalDetailsRequired && isSelfEmployed && (
        <>
          <NumberFormField
            name={'selfEmploymentNetIncome'}
            label={'SELF_EMPLOYMENT_NET_INCOME'}
            value={
              professionalDetails.selfEmploymentNetIncome?.value &&
              formatMoney(amountToValueMoneyMapper(professionalDetails.selfEmploymentNetIncome.value))
            }
            fieldVariant={fieldVariant}
            valueUnit={
              professionalDetails.selfEmploymentNetIncome?.value?.currency ??
              getMoneySymbolByCurrency(currency ?? Currency.EUR)
            }
          />
          <DateFormField
            format={'yyyy'}
            name={'selfEmploymentNetIncomeDate'}
            label={'SELF_EMPLOYMENT_NET_INCOME_DATE'}
            value={professionalDetails.selfEmploymentNetIncome?.date}
            fieldVariant={fieldVariant}
            views={['year']}
            placeholder={getTextIdTranslation('YEAR')}
          />
        </>
      )}
    </FormGridContainer>
  );
}
